import React, { useEffect, useRef, useState } from 'react';
import { fmtPct, fmtMktCap, fmtNum, fmtPrice, fmtLargeNum } from '../lib/colors';

const LOGO_BASE = 'https://assets.parqet.com/logos/symbol/';

function MetricRow({ label, value, positive, negative, mono = true }) {
  let cls = '';
  if (positive) cls = 'pos';
  else if (negative) cls = 'neg';
  return (
    <div className="stonks-hovercard-kv">
      <span className="stonks-hovercard-kv-label">{label}</span>
      <span className={`stonks-hovercard-kv-value${cls ? ` ${cls}` : ''}`}
        style={mono ? { fontFamily: 'var(--s-mono)' } : {}}>
        {value}
      </span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="stonks-hovercard-section-title">{title}</div>
      <div className="stonks-hovercard-grid">
        {children}
      </div>
    </div>
  );
}

function W52Bar({ low, high, current }) {
  if (!low || !high || !current) return null;
  const pct = Math.max(0, Math.min(100, ((current - low) / (high - low)) * 100));
  return (
    <div className="stonks-52w-bar-wrap">
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--s-text-muted)', marginBottom: 2 }}>
        <span>52W Range</span>
        <span style={{ fontFamily: 'var(--s-mono)', color: 'var(--s-text-dim)' }}>
          {Math.round(pct)}% of range
        </span>
      </div>
      <div className="stonks-52w-bar-track">
        <div className="stonks-52w-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="stonks-52w-labels">
        <span>{fmtPrice(low)}</span>
        <span>{fmtPrice(high)}</span>
      </div>
    </div>
  );
}

export default function HoverCard({
  ticker,
  quickQuote,
  info,
  loading,
  pos,
  visible,
  interactive = false,
  onClose,
  taxonomyInfo = null,
  transactions = [],
}) {
  const cardRef = useRef(null);
  const [cardPos, setCardPos] = useState({ top: 0, left: 0 });
  const [logoFailed, setLogoFailed] = useState(false);
  const [showAllTx, setShowAllTx] = useState(false);

  // Reset logo when ticker changes
  useEffect(() => {
    setLogoFailed(false);
    setShowAllTx(false);
  }, [ticker]);

  // Recompute card position so it doesn't go off-screen
  useEffect(() => {
    if (!visible || !pos) return;
    const card = cardRef.current;
    const cw = card ? card.offsetWidth : 300;
    const ch = card ? card.offsetHeight : 450;
    const gap = 16;

    let left = pos.x + gap;
    let top = pos.y - Math.min(ch / 2, pos.y - gap);

    if (left + cw > window.innerWidth - gap) {
      left = pos.x - cw - gap;
    }
    if (left < gap) left = gap;
    if (top + ch > window.innerHeight - gap) {
      top = window.innerHeight - ch - gap;
    }
    if (top < gap) top = gap;

    setCardPos({ top, left });
  }, [pos, visible]);

  if (!ticker) return null;

  const price = info?.price ?? quickQuote?.price;
  const change = info?.change ?? quickQuote?.change;
  const changePct = info?.changePercent ?? quickQuote?.changePercent;
  const name = info?.name ?? quickQuote?.shortName ?? ticker;
  const isPos = changePct >= 0;
  const yahooUrl = `https://finance.yahoo.com/quote/${encodeURIComponent(ticker)}`;

  return (
    <div
      ref={cardRef}
      className={`stonks-hovercard ${visible ? 'visible' : 'hidden'}${interactive ? ' interactive' : ''}`}
      style={{ top: cardPos.top, left: cardPos.left }}
      onClick={interactive ? e => e.stopPropagation() : undefined}
    >
      {/* Header */}
      <div className="stonks-hovercard-header">
        <div className="stonks-hovercard-logo">
          {!logoFailed ? (
            <img
              src={`${LOGO_BASE}${ticker}`}
              alt=""
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <span style={{ fontWeight: 700, fontSize: 14 }}>{ticker.charAt(0)}</span>
          )}
        </div>
        <div className="stonks-hovercard-name">
          <div className="stonks-hovercard-ticker">{ticker}</div>
          <div className="stonks-hovercard-full-name">{name}</div>
          <div className="stonks-hovercard-price-row">
            <span className="stonks-hovercard-price">{fmtPrice(price)}</span>
            <span className={`stonks-hovercard-change ${isPos ? 'pos' : 'neg'}`}>
              {isPos ? '+' : ''}{fmtNum(changePct, 2)}%
              &nbsp;({isPos ? '+' : ''}{fmtNum(change, 2)})
            </span>
          </div>
        </div>
        {interactive && (
          <button
            className="stonks-hovercard-close"
            onClick={onClose}
            aria-label="Close details"
            title="Close"
          >
            ✕
          </button>
        )}
      </div>

      {/* Body */}
      <div className="stonks-hovercard-body">
        {/* Quick info always visible from market data */}
        {quickQuote?.marketCap && (
          <div className="stonks-hovercard-grid">
            <MetricRow label="Mkt Cap" value={fmtMktCap(quickQuote.marketCap)} />
          </div>
        )}

        {/* GICS classification — Yahoo Finance taxonomy with Other / NA fallback */}
        {(() => {
          const gicsSector = taxonomyInfo?.sector || 'Other';
          const gicsSub = taxonomyInfo?.subIndustry || 'NA';
          return (
            <div className="stonks-hovercard-gics">
              {gicsSector && <span className="stonks-hovercard-gics-sector">{gicsSector}</span>}
              {gicsSub    && <span className="stonks-hovercard-gics-sub">{gicsSub}</span>}
            </div>
          );
        })()}

        {/* Loading skeleton when fetching details */}
        {loading && !info && (
          <div className="stonks-hovercard-loading">
            <div className="stonks-skeleton" style={{ height: 34, width: '100%' }} />
            <div className="stonks-skeleton" style={{ height: 60, width: '100%' }} />
            <div className="stonks-skeleton" style={{ height: 60, width: '100%' }} />
          </div>
        )}

        {/* Detailed info */}
        {info && (
          <>
            {info.longBusinessSummary ? (
              <p className="stonks-hovercard-desc">{info.longBusinessSummary}</p>
            ) : (
              <p className="stonks-hovercard-desc">
                No profile summary returned.{' '}
                <a href={yahooUrl} target="_blank" rel="noreferrer">Open Yahoo Finance</a>
              </p>
            )}

            {transactions.length > 0 && (
              <Section title="Transaction History">
                <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(showAllTx ? transactions : transactions.slice(0, 10)).map((tx, idx) => (
                    <div className="stonks-hovercard-tx-row" key={`${tx.ticker}-${tx.date}-${tx.amount}-${idx}`}>
                      <span className="stonks-hovercard-tx-date">{tx.date || '—'}</span>
                      <span className={`stonks-hovercard-tx-type ${(tx.type === 'purchase' || tx.type === 'buy') ? 'pos' : 'neg'}`}>
                        {tx.type || '—'}
                      </span>
                      <span className="stonks-hovercard-tx-amt">{tx.amount || '—'}</span>
                    </div>
                  ))}
                  {transactions.length > 10 && !showAllTx && (
                    <button
                      className="btn-stonks btn-stonks-ghost"
                      style={{ alignSelf: 'flex-start', padding: '3px 8px', fontSize: 10 }}
                      onClick={() => setShowAllTx(true)}
                    >
                      Show all ({transactions.length})
                    </button>
                  )}
                  {transactions.length > 10 && showAllTx && (
                    <button
                      className="btn-stonks btn-stonks-ghost"
                      style={{ alignSelf: 'flex-start', padding: '3px 8px', fontSize: 10 }}
                      onClick={() => setShowAllTx(false)}
                    >
                      Show less
                    </button>
                  )}
                </div>
              </Section>
            )}

            {/* Valuation */}
            <Section title="Valuation">
              <MetricRow label="P/E (Trail.)" value={info.trailingPE ? fmtNum(info.trailingPE) : '—'} />
              <MetricRow label="P/E (Fwd.)"  value={info.forwardPE  ? fmtNum(info.forwardPE)  : '—'} />
              <MetricRow label="P/B"          value={info.priceToBook ? fmtNum(info.priceToBook) : '—'} />
              <MetricRow label="P/S"          value={info.priceToSalesTrailing12Months ? fmtNum(info.priceToSalesTrailing12Months) : '—'} />
              <MetricRow label="EV/EBITDA"    value={info.enterpriseToEbitda ? fmtNum(info.enterpriseToEbitda) : '—'} />
              <MetricRow label="PEG"          value={info.pegRatio ? fmtNum(info.pegRatio) : '—'} />
            </Section>

            {/* Growth */}
            <Section title="Growth (YoY)">
              <MetricRow
                label="Revenue"
                value={info.revenueGrowth != null ? fmtPct(info.revenueGrowth * 100) : '—'}
                positive={info.revenueGrowth > 0}
                negative={info.revenueGrowth < 0}
              />
              <MetricRow
                label="Earnings"
                value={info.earningsGrowth != null ? fmtPct(info.earningsGrowth * 100) : '—'}
                positive={info.earningsGrowth > 0}
                negative={info.earningsGrowth < 0}
              />
              <MetricRow
                label="Rev (Qtrly)"
                value={info.revenueQuarterlyGrowth != null ? fmtPct(info.revenueQuarterlyGrowth * 100) : '—'}
                positive={info.revenueQuarterlyGrowth > 0}
                negative={info.revenueQuarterlyGrowth < 0}
              />
              <MetricRow
                label="EPS (Qtrly)"
                value={info.earningsQuarterlyGrowth != null ? fmtPct(info.earningsQuarterlyGrowth * 100) : '—'}
                positive={info.earningsQuarterlyGrowth > 0}
                negative={info.earningsQuarterlyGrowth < 0}
              />
            </Section>

            {/* Financials */}
            <Section title="Financials">
              <MetricRow label="Gross Margin"  value={info.grossMargins != null ? fmtNum(info.grossMargins * 100) + '%' : '—'} />
              <MetricRow label="Op. Margin"    value={info.operatingMargins != null ? fmtNum(info.operatingMargins * 100) + '%' : '—'} />
              <MetricRow label="ROE"            value={info.returnOnEquity != null ? fmtNum(info.returnOnEquity * 100) + '%' : '—'} />
              <MetricRow label="ROA"            value={info.returnOnAssets != null ? fmtNum(info.returnOnAssets * 100) + '%' : '—'} />
              <MetricRow label="D/E Ratio"      value={info.debtToEquity ? fmtNum(info.debtToEquity / 100) + 'x' : '—'} />
              <MetricRow label="Current Ratio"  value={info.currentRatio ? fmtNum(info.currentRatio) + 'x' : '—'} />
              <MetricRow label="Free Cash Flow" value={fmtLargeNum(info.freeCashflow)} />
              <MetricRow label="Cash"           value={fmtLargeNum(info.totalCash)} />
            </Section>

            {/* Momentum */}
            <Section title="Momentum & Yield">
              <MetricRow label="Beta"      value={info.beta ? fmtNum(info.beta) : '—'} />
              <MetricRow label="Div Yield" value={info.dividendYield != null ? fmtNum(info.dividendYield * 100, 2) + '%' : '—'} />
              <MetricRow label="Short Ratio" value={info.shortRatio ? fmtNum(info.shortRatio) + 'x' : '—'} />
              <MetricRow label="Avg Volume"  value={fmtLargeNum(info.averageVolume)} />
            </Section>

            {/* 52-week range */}
            <W52Bar
              low={info.week52Low}
              high={info.week52High}
              current={price}
            />
          </>
        )}
      </div>
    </div>
  );
}
